import { ApolloError } from '@apollo/client';
import {
  GetDarkPoolTradesBaselineQuery,
  GetDarkPoolTradesFullQuery,
  GetTickerDarkPoolTradesQuery,
  GetFullFeedTickerEquityPrintFlowQuery,
} from 'types/generated/client';
import { Viewer } from 'constants/user';
import { DarkPoolPercentages } from 'constants/payloads/darkPool';
import { TickerDetailsInfo } from 'utils/shared/tickers/buildTickerDetailsMap';
import {
  ColumnType,
  SortDirection,
  SignaturePrintStatus,
  Sentiment,
  MobileLayout,
} from 'utils/shared/trades/types';

export const DEFAULT_FLOW_KEY_INDEX = 0;
export const DATE_COL = { name: 'Date', sortKey: 'date', columnType: ColumnType.Date };
export const DARK_POOL_TABLE_HEADERS = [
  { name: 'Time', sortKey: 'timestampIso', columnType: ColumnType.Date },
  { name: 'Ticker', sortKey: 'ticker', columnType: ColumnType.Text },
  // { name: 'Sector', sortKey: 'sector', columnType: ColumnType.Text },
  { name: 'Price', sortKey: 'price', columnType: ColumnType.Number },
  { name: 'Size', sortKey: 'size', columnType: ColumnType.Number },
  { name: 'Amount', sortKey: 'amount', columnType: ColumnType.Number },
  { name: 'Levels', sortKey: '', columnType: ColumnType.Number },
];
export const DEFAULT_FLOW_KEY = DARK_POOL_TABLE_HEADERS[DEFAULT_FLOW_KEY_INDEX].sortKey;

type GeneratedDarkPoolTrade =
  | Omit<GetDarkPoolTradesBaselineQuery['equity_print_feed_baseline'][0], '__typename'>
  | Omit<GetDarkPoolTradesFullQuery['equity_print_flow_full'][0], '__typename'>
  | Omit<GetTickerDarkPoolTradesQuery['ticker_equity_print_flow'][0], '__typename'>
  | Omit<GetFullFeedTickerEquityPrintFlowQuery['full_feed_equity_print_flow'][0], '__typename'>;

// The generated numeric scalar is `any`; retain an explicit nullable contract at the UI boundary.
export type DarkPoolTrade = Omit<GeneratedDarkPoolTrade, keyof DarkPoolPercentages> &
  DarkPoolPercentages & {
    tickerDetails?: {
      name?: string | null;
      ticker?: string | null;
      sector?: string | null;
    } | null;
  };

export interface OwnProps {
  watchListTickers: string[];
  isWatchListOnly: boolean;
  setIsWatchListOnly?: (isWatchListOnly: boolean) => void;
  hasWatchListItems: boolean;
  viewer: Viewer;
  searchValue: string;
  includeDate?: boolean;
  right?: JSX.Element;
}

export interface DarkPoolTableHeaderProps {
  flowSortKey: string;
  setFlowSortKey: (key: string) => void;
  flowSortDirection: SortDirection;
  setFlowSortDirection: (direction: SortDirection) => void;
  isWatchListOnly: boolean;
  setIsWatchListOnly?: (isWatchListOnly: boolean) => void;
  hasWatchListItems: boolean;
  includeDate?: boolean;
  right?: JSX.Element;
  setShowInfo: any;
  setIsWatchListModalOpen?: (isOpen: boolean) => void;
  isEarningsOnly?: boolean;
  setIsEarningsOnly?: (value: boolean) => void;
  mobileLayout?: MobileLayout;
  setMobileLayout?: (mobileLayout: MobileLayout) => void;
  hideHeader?: boolean;
  hideColumnHeader?: boolean;
  hideLevelsColumn?: boolean;
  hideEarningsToggle?: boolean;
  title?: string;
  onDownload?: () => void;
}

interface DerivedPrintValues {
  // sector: string;
  signaturePrintStatus: SignaturePrintStatus;
  signaturePrintSentiment: Sentiment;
  signaturePrintNumber: number;
}

export interface DarkPoolTableRowProps {
  trade: DarkPoolTrade & DerivedPrintValues;
  watchListTickers: string[];
  signaturePrintCount: number;
  includeDate?: boolean;
  trackRowAsAnimated?: () => void;
  hasBeenAnimated?: boolean;
  setDarkPoolLevelTicker: (ticker: string) => void;
  showInfo: boolean;
  handleBookmark?: (symbol: string) => void;
  hideLevelsColumn?: boolean;
  hasEarnings: (ticker: string | null | undefined) => boolean;
  getEarningsData: (ticker: string | null | undefined) => any;
  getDaysToEarnings: (ticker: string | null | undefined) => number | null;
  tickerDetailsMap?: Record<string, TickerDetailsInfo>;
}

export interface Props extends OwnProps {
  hasAccess: boolean;
  trades: Array<DarkPoolTrade>;
  loading: boolean;
  error?: ApolloError;
  handleBookmark?: (symbol: string) => void;
  earningsMap?: Record<string, any>;
  tickerDetailsMap?: Record<string, TickerDetailsInfo>;
  permissionTitle?: string;
  permissionDescription?: React.ReactNode;
  setIsWatchListModalOpen?: (isOpen: boolean) => void;
  hideHeader?: boolean;
  hideLevelsColumn?: boolean;
  hideEarningsToggle?: boolean;
  title?: string;
}
